import { ColDef, ValueFormatterParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import useUiuxStore from "src/store/useUiuxStore";
import { CreateCellClickHandler, CreateCellDoubleClickHandler, CustomCellClickAction, CustomCellDoubleClickAction, RemoveRowHandler, InsertRowHandler, postSortHandler, useCellChangeHandler, useRowSelectionHandler } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { createSaveColumnState, defaultColDef, getDefaultRowIndexColumnConfig, getRowStyle, getRowStyleFlags, IndexParameterCell, onGridReady } from "src/utils/grid/gridStyleUtils";
import { useSAL2070_R01Store } from "./Store_SAL2070_R01";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { defaultSAL2070_R01Dto, SAL2070_R01Dto } from "./Dto_SAL2070_R01";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { formatDate } from "src/utils/string/stringUtil";
import { onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";
import { cellDateComparator } from "src/utils/grid/gridFunctionUtil";

const COLUMN_STATE_KEY = 'SAL2070_R01-detail-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<SAL2070_R01Dto> | null>;
}

export const DetailTable_SAL2070_R01 = (props: Props) => {

	const { gridRef } = props;

	const { 
			SAL2070_R01DetailList, setSAL2070_R01DetailList, 
			selectedSAL2070_R01DetailList, setSelectedSAL2070_R01DetailList, removeSelectedSAL2070_R01DetailList, addSelectedSAL2070_R01DetailList, initSelectedSAL2070_R01DetailList,
			deletedSAL2070_R01DetailList, setDeletedSAL2070_R01DetailList, addDeletedSAL2070_R01DetailList, removeDeletedSAL2070_R01DetailList,
			insertSAL2070_R01DetailList, updateSAL2070_R01DetailList, removeSAL2070_R01DetailList,
			detailColumnState, setDetailColumnState,
			originalSAL2070_R01DetailList, setOriginalSAL2070_R01DetailList,
			detailScrollTop, setDetailScrollTop,
		} = useSAL2070_R01Store();
	const { setCodeSearchModalProps } = useUiuxStore();
	const [clickable, setClickable] = useState(true);

	//const gridRef = useRef<AgGridReact>(null);
	const gridWrapperRef = useRef<HTMLDivElement>(null); // 1. div를 위한 ref 생성
	const [hoveredDetailRowIndex, setHoveredDetailRowIndex] = useState<number | null>(null);

	const {
		getRowId,
		settingRowIndex, 
		settingSecondColumnCount,
		createRowClassRules,
		paddedGridData,
		renderIndexCell,
		defaultRowIndexCellButton,
		rowIndexCellButtonType1,
		rowIndexCellButtonType2,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'tradeNo',
		gridData: SAL2070_R01DetailList,
		defaultGridData: defaultSAL2070_R01Dto,
		selectedRow: selectedSAL2070_R01DetailList,
		originalGridData: originalSAL2070_R01DetailList,
		updateRow: (index: number, data: any) => updateSAL2070_R01DetailList(index, data),
	});

	const headerNames: Record<string, string> = {
		rowIndex: "",
		tradeNo: '거래전표번호',
		tradeDt: '거래일자',
		itemNm: '거래처',
		totalAmt: '거래금액',
	}

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'tradeNo')(params), []);

	const baseColumnDefs: ColDef<SAL2070_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '거래전표번호', colId: 'tradeNo', field: 'tradeNo', width: 130,
			cellStyle: (p) => getCellStyle(p, 'tradeNo'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '거래일자', colId: 'tradeDt', field: 'tradeDt', width: 100,
			cellStyle: (p) => getCellStyle(p, 'tradeDt'),
			headerClass: aggridHeaderClass,
			valueFormatter: (p: ValueFormatterParams<SAL2070_R01Dto>) =>
				p.data?.tradeDt ? formatDate(new Date(p.data.tradeDt)) : '',
			comparator: cellDateComparator,
		},
		{
			headerName: '거래품명', colId: 'itemNm', field: 'itemNm', width: 250,
			cellStyle: (p) => getCellStyle(p, 'itemNm'),
			headerClass: aggridHeaderClass,
			valueFormatter: (params: ValueFormatterParams<SAL2070_R01Dto>) => {
				if (params.node?.rowPinned) {
					return '';
				}
				if (!params.data) return '';
				const data = params.data as SAL2070_R01Dto;
				return (data.itemCount ?? 0) > 1
					? data.itemNm + ' 외 ' + ((data.itemCount ?? 0) - 1) + '건'
					: data.itemNm;
			},
		},
		{
			headerName: '거래금액', colId: 'totalAmt', field: 'totalAmt', width: 130,
			cellStyle: (p) => getCellStyle(p, 'totalAmt'),
			editable: false,
			headerClass: aggridHeaderClass,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
		},
	], [getCellStyle, cellDateComparator]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<SAL2070_R01Dto>[] = useMemo(() => {
		if (detailColumnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<SAL2070_R01Dto>({
			baseColumnDefs,
			columnState: detailColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [detailColumnState, settingRowIndex, baseColumnDefs, headerNames]);
	

	const handleRowClick = useRowSelectionHandler({
		dataList: SAL2070_R01DetailList,
		idKey: 'tradeNo',
		selectedList: selectedSAL2070_R01DetailList,
		deletedList: deletedSAL2070_R01DetailList,
		addSelected: addSelectedSAL2070_R01DetailList,
		removeSelected: removeSelectedSAL2070_R01DetailList,
		clickable, setClickable
	});

	const pinnedBottomRowData = useMemo(() => {

		const totalAmt : number = SAL2070_R01DetailList
			.reduce((sum, item) => sum + (deletedSAL2070_R01DetailList.includes(item.itemCd) ? 0 : Number(item.totalAmt) || 0), 0);

		return [{
			itemCd: SAL2070_R01DetailList.length.toLocaleString() + "건",
			totalAmt: totalAmt
		}];	  
	}, [SAL2070_R01DetailList, deletedSAL2070_R01DetailList,]);

	return (
		<GridContextWrapper<SAL2070_R01Dto>
			enableCellCalculation
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			setPaddedGridData={setSAL2070_R01DetailList}
			initSelected={initSelectedSAL2070_R01DetailList}
			defaultRowData={defaultSAL2070_R01Dto}
			baseColumnDefs={detailColumnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setDetailColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			
			<Grid_Touch<SAL2070_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				
				rowClassRules={createRowClassRules<SAL2070_R01Dto>({
					idKey: 'tradeNo',
					selectedList: selectedSAL2070_R01DetailList,
					deletedList: [],
					
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRowData}    

				// Grid_Select에 공통 로직 처리를 위한 props 전달
				setHoveredRowIndex={setHoveredDetailRowIndex}		//마우스 호버 상태 이벤트를 위한 저장
				columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
				setColumnState={setDetailColumnState}				//컬럼 상태 저장
				scrollTop={detailScrollTop}
				setScrollTop={setDetailScrollTop}
			/>
		</GridContextWrapper>
	)
}
